import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { VenueContactsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { Badge } from '@/components/event_contact_type/badge'
import { VenueContactFilters } from '@/types'
import { contactTypes } from './contact_types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  venueId: string
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, venueId, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<VenueContactsQuery>(
    Query,
    { variables: { venueId, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openEdit,
    setOpenEdit
  } = useTable({ pageInfo: data?.venues.nodes[0].venueContacts.pageInfo })
  const [venueContactId, setVenueContactId] = useState<string>()

  const editVenueContact = (id: string) => {
    if (!updateable) return

    setVenueContactId(id)
    setOpenEdit(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<VenueContactFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Type' }, { name: 'Name' }, { name: 'Email' }, { name: 'Phone' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.venues.nodes[0].venueContacts.nodes = dedupe([
              ...previousData.venues.nodes[0].venueContacts.nodes,
              ...fetchMoreResult.venues.nodes[0].venueContacts.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, contactTypeEq: value })),
            type: 'select',
            options: Object.keys(contactTypes).map(ct => ({
              value: ct,
              display: <Badge eventContact={{ name: contactTypes[ct].name, color: contactTypes[ct].color }} />
            }))
          },
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'What did you call them?'
          },
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, emailCont: value })),
            type: 'text',
            placeholder: 'Did you email them?'
          },
          {
            index: 3,
            setValue: (set, value) => set(f => ({ ...f, phoneCont: value })),
            type: 'text',
            placeholder: 'Did you ring them?'
          },
        ]}
        rows={data.venues.nodes[0].venueContacts.nodes.map(venueContact => (
          {
            id: venueContact.id,
            columns: [
              <Badge
                eventContact={{
                  name: contactTypes[venueContact.contactType].name,
                  color: contactTypes[venueContact.contactType].color
                }}
              />,
              venueContact.name,
              venueContact.email,
              venueContact.phone,
            ]
          }
        ))}
        onRowClick={editVenueContact}
      />
      { venueContactId && <EditDialog id={venueContactId} open={openEdit} setOpen={setOpenEdit} venueId={venueId} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} venueId={venueId} />
    </>
  )
}
